import React from 'react';
import PropTypes from 'prop-types';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { loadRecaptcha, executeRecaptcha } from 'utils/recaptcha_helpers';
import { trackOptimizelyEvent, OptimizelyConstants } from 'utils/OptimizelyHelpers';
import { logProPurchaseBuyNowClicked, logPurchaseSuccess } from '@alltrails/analytics/amplitudeFunctions';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { validatePaymentForm } from 'utils/form_validaters/payment_form_helper';
import { hasErrors } from 'utils/form_validaters/user_form_helper';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import { WebProPurchaseLocation } from '@alltrails/analytics/amplitudeEnums';
import Button from 'components/shared/Button';
import PlanCode from 'types/PlanCode';
import PaymentFinePrint from 'components/PaymentFinePrint';
import * as baseStyles from '../../../../styles/baseStyles.module.scss';
import * as formStyles from '../../styles/styles.module.scss';
import * as styles from './styles/styles.module.scss';
import PlanTypeCard from '../PlanTypeCard';
import PlanTypeCardCollection from '../PlanTypeCardCollection';
import PaymentTabs from 'components/PaymentTabs';
import Wrapper from '../../Wrapper';
import CreditCardForm from '../../CreditCardForm/CreditCardForm';
import ExternalPayForm from '../../ExternalPayForm/ExternalPayForm';
import CouponCode from '../../CouponCode';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

const SUCCESS_URL = '/pro/welcome';
const GIFT_CARD = 'GiftCard';

function getActivePlanType(planTypes, activePlanType) {
  return planTypes.find(planType => planType.planCode === activePlanType);
}

class PaymentForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: {
        name: '',
        number: '',
        cvv: '',
        postalCode: ''
      },
      name: '',
      number: '',
      cvv: '',
      postalCode: '',
      couponError: '',
      serverError: null,
      isCouponInputExpanded: false,
      submitting: false,
      paypalToken: null,
      paypal: null,
      applePay: null,
      amazonPayToken: null,
      applePayToken: null,
      activePaymentTab: 0,
      editingPlanType: false,
      disableSubmit: false,
      recaptchaObject: null,
      expiration: ''
    };
  }

  componentDidMount() {
    if (this.props.paymentTypes[1].showTab && typeof recurly !== 'undefined') {
      recurly.configure(this.props.recurlyPublicKey);
      this.setupThirdPartyPayments();
    }

    if (this.props.enableRecaptcha && !this.state.recaptchaObject) {
      loadRecaptcha(recaptchaObject => {
        this.setState({ disableSubmit: false, recaptchaObject });
      });
    }
  }

  setupThirdPartyPayments() {
    this.setupApplePay();
    const planType = getActivePlanType(this.props.planTypes, this.props.activePlanType);
    const paypal = recurly.PayPal({
      display: {
        displayName: 'AllTrails Pro',
        amount: planType.priceTotal
      }
    });
    paypal.on('error', err => {
      console.error(err);
    });
    paypal.on('token', token => {
      this.setState({ paypalToken: token.id }, this.loadRecaptchaAndSubmit);
    });

    this.setState({ paypal });
  }

  setupApplePay() {
    let applePay;
    const planType = getActivePlanType(this.props.planTypes, this.props.activePlanType);
    if (this.props.paymentTypes[2].showTab) {
      let priceTotal = planType.priceTotal;
      if (this.props.verifiedCoupon && this.props.verifiedCoupon.discountedPricePerYear) {
        priceTotal = this.props.verifiedCoupon.discountedPricePerYear;
      }
      try {
        applePay = recurly.ApplePay({
          country: 'US', // this is the merchant's country code, so will remain US
          currency: this.props.currencyCode,
          label: 'AllTrails Pro',
          total: priceTotal
        });
        applePay.on('error', err => {
          console.error('Apple Pay error', err);
        });
        applePay.on('token', token => {
          // Submit the token to server when flow is completed successfully
          this.setState({ applePayToken: token.id }, this.loadRecaptchaAndSubmit);
        });
      } catch (error) {
        console.error(error);
      }
    }

    this.setState({ applePay });
  }

  handleCreditCardFormChange = event => {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, this.state.errors, this.props.text.creditCardForm);
    this.setState({ ...payload });
  };

  handleApplePayClick = () => {
    if (!this.state.applePay) {
      return;
    }

    this.state.applePay.begin();
  };

  handlePaypalClick = () => {
    if (!this.state.paypal) {
      return;
    }

    this.state.paypal.start();
  };

  handleAmazonClick = amazonPayToken => {
    this.setState({ amazonPayToken }, this.loadRecaptchaAndSubmit);
  };

  trackConversionInTatari() {
    // Safety first - Wrap anything Tatari-related in a try/catch
    try {
      const activePlan = getActivePlanType(this.props.planTypes, this.props.activePlanType);
      if (window.tatari) {
        const plan = activePlan.recurlyPlanCode;
        const total = activePlan.priceTotal;
        window.tatari.track('purchase', { plan, total });
      }
    } catch (error) {
      console.error('Error tracking conversion in Tatari', error);
    }
  }

  handlePaymentFormSuccess = () => {
    const { couponCode, optimizelyInstance, optimizelyUserId, locale, activePlanType, optimizelyAttributeData, webPurchaseLocation } = this.props;

    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'submit']);

    this.trackConversionInTatari();

    const recurlyPlanCode = getActivePlanType(this.props.planTypes, this.props.activePlanType).recurlyPlanCode;

    logPurchaseSuccess({ campaign_id: couponCode, product_id: recurlyPlanCode, web_purchase_location: webPurchaseLocation });

    if (this.props.optimizelyInstance) {
      // Track any pro conversion event, outside of any current tests
      trackOptimizelyEvent(
        optimizelyInstance,
        optimizelyUserId,
        OptimizelyConstants.EVENTS.PRO_CONVERSION,
        OptimizelyConstants.OUTCOMES.SUCCESS,
        optimizelyAttributeData
      );

      if (activePlanType !== PlanCode.SevenDayTrial) {
        const eventName = {
          [PlanCode.Monthly]: OptimizelyConstants.EVENTS.PRO_CONVERSION_MONTHLY,
          [PlanCode.Annual]: OptimizelyConstants.EVENTS.PRO_CONVERSION_ANNUAL,
          [PlanCode.ThreeYear]: OptimizelyConstants.EVENTS.PRO_CONVERSION_3_YEAR
        }[activePlanType];

        trackOptimizelyEvent(optimizelyInstance, optimizelyUserId, eventName);
      }
    }
    setTimeout(() => {
      window.location.href = LanguageSupportUtil.wrapUrlSafe(SUCCESS_URL, locale);
    }, 200); // small delay to let Optimizely event go through
  };

  handlePaymentFormError = err => {
    ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'payment_error']);
    let { errors, serverError } = this.state;

    let errorObj;
    try {
      errorObj = JSON.parse(err.responseText);
    } catch (e) {
      errorObj = false;
    }

    if (errorObj) {
      if (errorObj.base) {
        serverError = errorObj.base[0];
      }
      if (errorObj.cc_number) {
        errors.number = errorObj.cc_number[0];
      }

      if (errorObj.cc_expiration_month) {
        errors.expiration = errorObj.cc_expiration_month[0];
      }

      if (errorObj.cc_expiration_year) {
        errors.expiration = errorObj.cc_expiration_year[0];
      }

      if (errorObj.cc_csc) {
        errors.cvv = errorObj.cc_csc[0];
      }
      if (errorObj.first_name || errorObj.last_name) {
        const nameErrors = [];
        if (errorObj.first_name) {
          nameErrors.push(`${StringConstantsMixin.strings.SIGNUP.FIRST_NAME}: ${errorObj.first_name[0]}`);
        }
        if (errorObj.last_name) {
          nameErrors.push(`${StringConstantsMixin.strings.SIGNUP.LAST_NAME}: ${errorObj.last_name[0]}`);
        }
        errors.name = nameErrors.join(', ');
      }
    } else {
      serverError = this.props.text.serverError;
    }

    this.setState({ errors, serverError, submitting: false });
  };

  getCouponCodeInput() {
    return (
      <CouponCode
        applyHandler={this.props.handleApplyCoupon}
        changeHandler={this.props.handleCouponCodeChange}
        errorMessage={this.props.couponError}
        expanded={this.props.isCouponInputExpanded}
        isSuccess={this.props.isCouponApplied}
        removeHandler={this.props.handleRemoveCoupon}
        value={this.props.couponCode}
      />
    );
  }

  getFirstAndLastName() {
    const fullName = this.state.name.split(' ');
    const billing_first_name = fullName[0];
    const billing_last_name = fullName[fullName.length - 1];

    return { billing_first_name, billing_last_name };
  }

  noGiftCard() {
    if (!this.props.verifiedCoupon) {
      return true;
    }

    return this.props.verifiedCoupon.type !== GIFT_CARD;
  }

  noAlternatePayment() {
    return this.state.applePayToken === null && this.state.amazonPayToken === null && this.state.paypalToken === null;
  }

  failedFormValidation() {
    let validation;
    let validationErrors = { ...this.state.errors };

    for (const name in this.state.errors) {
      validation = validatePaymentForm(name, this.state[name], this.state.errors, this.props.text.creditCardForm);
      validationErrors = { ...validationErrors, ...validation.errors };
    }
    this.setState({ errors: validationErrors });
    return hasErrors(validationErrors);
  }

  loadRecaptchaAndSubmit = e => {
    if (e) {
      e.preventDefault();
    }

    if (this.state.submitting) {
      return;
    }

    this.setState({ submitting: true }, () => ServerCommunicationUtil.handleGaqPush(['_trackEvent', 'pro_step_2', 'submit']));
    if (this.noAlternatePayment() && this.noGiftCard()) {
      if (this.failedFormValidation()) {
        this.setState({ submitting: false });
        return;
      }
    }

    if (this.props.enableRecaptcha) {
      executeRecaptcha(this.state.recaptchaObject, 'userPaymentAction').then(this.submitPayment);
    } else {
      this.submitPayment();
    }
  };

  submitPayment = (recaptchaToken = null) => {
    const { activePlanType, forceMonthlyPricePoint, planTypes } = this.props;

    const { billing_first_name, billing_last_name } = this.getFirstAndLastName();

    const [cc_expiration_month, cc_expiration_year] = this.state.expiration.split(' / ');

    const submissionData = {
      plan: activePlanType,
      recurly_plan_code: planTypes.find(plan => plan.planCode === activePlanType).recurlyPlanCode,
      force_monthly_price_point: forceMonthlyPricePoint,
      paypal_token: this.state.paypalToken,
      apple_pay_token: this.state.applePayToken,
      amazon_pay_token: this.state.amazonPayToken,
      recaptcha: recaptchaToken,
      user: {
        user_entered_coupon_code: this.props.verifiedCoupon ? this.props.verifiedCoupon.code : null,
        cc_number: this.state.number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: this.state.cvv,
        zip: this.state.postalCode,
        billing_first_name,
        billing_last_name
      }
    };

    if (this.props.handlePayment) {
      this.props.handlePayment(submissionData).catch(err => {
        this.handlePaymentFormError(err);
      });

      return;
    }

    ServerCommunicationUtil.putApiEndpoint('/pro', submissionData, this.handlePaymentFormSuccess, this.handlePaymentFormError, () => {}, {
      'X-Language-Locale': this.props.locale
    });
  };

  renderServerError() {
    return <div className={this.state.serverError ? styles.serverError : ''}>{this.state.serverError}</div>;
  }

  renderExternalPayForm() {
    return (
      <div>
        <div>
          <div className={formStyles.promptHead}>
            <FormattedMessage defaultMessage="Choose your payment method:" />
          </div>
          <div className={formStyles.prompt}>
            <FormattedMessage defaultMessage="By clicking an option below you will be redirected to either PayPal or Apple Pay to complete your purchase." />
          </div>
        </div>
        {this.props.isCouponInputShown && this.getCouponCodeInput()}
        <ExternalPayForm
          text={this.props.text}
          paypalHandler={this.handlePaypalClick}
          applePayHandler={this.handleApplePayClick}
          amazonPayHandler={this.handleAmazonClick}
          showApplePay={this.props.paymentTypes[2].showTab}
          showAmazonPay={this.props.paymentTypes[3].showTab}
          amazonSellerId={this.props.amazonSellerId}
          amazonClientId={this.props.amazonClientId}
        />
        {this.renderServerError()}
      </div>
    );
  }

  renderCreditCardForm() {
    return (
      <form id="payment-form" onSubmit={this.loadRecaptchaAndSubmit}>
        <CreditCardForm
          expiration={this.state.expiration}
          errors={this.state.errors}
          changeHandler={this.handleCreditCardFormChange}
          name={this.state.name}
          number={this.state.number}
          cvv={this.state.cvv}
          postalCode={this.state.postalCode}
          submitHandler={this.loadRecaptchaAndSubmit}
          setError={(name, error) => this.setState({ errors: { ...this.state.errors, [name]: error } })}
          toggleCouponCode={this.props.toggleCouponCode}
          isCouponInputExpanded={this.state.isCouponInputExpanded}
          isCouponInputShown={this.props.isCouponInputShown}
        />
        {this.props.isCouponInputShown && this.getCouponCodeInput()}
        {this.renderServerError()}
        <div className={styles.submit}>
          <Button
            disabled={this.state.submitting || this.state.disableSubmit}
            onClick={() => {
              logProPurchaseBuyNowClicked({ web_purchase_location: this.props.webPurchaseLocation });
            }}
            type="submit"
            variant="primary"
          >
            {this.props.submitText}
          </Button>
        </div>
      </form>
    );
  }

  editPlanType = () => {
    this.setState({ editingPlanType: !this.state.editingPlanType });
  };

  handleTypeClick = activePlanType => {
    this.editPlanType();
    this.props.updateActivePlan(activePlanType);
  };

  getPlanTypeCard() {
    if (this.state.editingPlanType) {
      return (
        <div className={styles.planTypeCardCollection}>
          <PlanTypeCardCollection
            planTypes={this.props.planTypes}
            handler={this.handleTypeClick}
            activePlanType={this.props.activePlanType}
            currencyCode={this.props.currencyCode}
            verifiedCoupon={this.state.verifiedCoupon}
            isCouponApplied={this.state.isCouponApplied}
            isSuccess={this.state.isCouponApplied}
          />
        </div>
      );
    }

    return (
      <PlanTypeCard
        staticCard
        plan={getActivePlanType(this.props.planTypes, this.props.activePlanType)}
        currencyCode={this.props.currencyCode}
        verifiedCoupon={this.props.verifiedCoupon}
        isCouponApplied={this.props.isCouponApplied}
      />
    );
  }

  render() {
    // fine print is hidden on the gifting pages
    const footText = !this.props.hideFinePrint ? (
      <PaymentFinePrint
        activePlanType={this.props.activePlanType}
        currencyCode={this.props.currencyCode}
        planTypes={this.props.planTypes}
        verifiedCoupon={this.props.verifiedCoupon}
        isCouponApplied={this.props.isCouponApplied}
      />
    ) : null;

    return (
      <Wrapper text={this.props.text} foot={footText}>
        <div className={baseStyles.section}>
          <div className={styles.planType}>
            {this.getPlanTypeCard()}
            {this.props.planTypes.length > 1 && (
              <div className={classNames(baseStyles.link, styles.changePlan)} onClick={this.editPlanType}>
                <FormattedMessage defaultMessage="Change Plan" />
              </div>
            )}
          </div>
          <div className={baseStyles.row}>
            <PaymentTabs
              tabs={this.props.paymentTypes}
              handler={activePaymentTab => {
                this.setState({ activePaymentTab });
              }}
              activeTab={this.state.activePaymentTab}
              showPayWithCard={this.props.showPayWithCard}
            />
          </div>
          <div className={styles.body}>{this.state.activePaymentTab === 0 ? this.renderCreditCardForm() : this.renderExternalPayForm()}</div>
        </div>
      </Wrapper>
    );
  }
}

PaymentForm.propTypes = {
  hideFinePrint: PropTypes.bool,
  activePlanType: PropTypes.string.isRequired,
  amazonClientId: PropTypes.string,
  couponError: PropTypes.string,
  currencyCode: PropTypes.string,
  goBack: PropTypes.func,
  handleApplyCoupon: PropTypes.func,
  handleCouponCodeChange: PropTypes.func,
  handleRemoveCoupon: PropTypes.func,
  toggleCouponCode: PropTypes.func,
  isCouponApplied: PropTypes.bool,
  isCouponInputExpanded: PropTypes.bool,
  isCouponInputShown: PropTypes.bool,
  handlePayment: PropTypes.func,
  paymentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      tab: PropTypes.number,
      iconPath: PropTypes.string
    })
  ),
  planTypes: PropTypes.arrayOf(
    PropTypes.shape({
      planCode: PropTypes.string,
      priceMonth: PropTypes.string,
      priceTotal: PropTypes.string,
      numberOfMonths: PropTypes.string,
      showDiscount: PropTypes.bool
    })
  ),
  recurlyPublicKey: PropTypes.string,
  verifiedCoupon: PropTypes.shape({
    discountPercentage: PropTypes.number
  }),
  optimizelyInstance: PropTypes.object,
  optimizelyUserId: PropTypes.string,
  webProPurchaseLocation: PropTypes.oneOf([WebProPurchaseLocation.GiftPage, WebProPurchaseLocation.ProPage])
};

PaymentForm.defaultProps = {
  handlePayment: null,
  hideFinePrint: false
};

export default PaymentForm;
